import React, {FC, useState, useEffect} from 'react'
import {showSwal} from '../../../../ui-kit'
import {useParams, useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Resource} from '../resources/core/_models'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {getResourceById, createResource, updateResourceById} from '../resources/core/_requests'
import ResourcesDetailView from './components/ResourcesDetailView'
import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'

const isHtmlEmpty = (html?: string | null) => {
  if (!html) return true
  const clean = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim()
  return clean === ''
}

const ResourcesDetailPage: FC<{subject: string}> = ({subject}) => {
  const {idResource} = useParams<{idResource: string}>()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)

  const isAddMode = !idResource
  const isDetailMode = !!idResource
  const [isEditMode, setIsEditMode] = useState(isAddMode)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)

  const [resourceDetail, setResourceDetail] = useState<Resource>({
    title: '',
    short_desc: '',
    image_url: '',
    download_url: '',
    is_video: 0,
    title_fr: null,
    short_desc_fr: null,
  })

  useEffect(() => {
    const loadDetail = async () => {
      if (!isDetailMode || !idResource) return
      setIsLoading(true)
      try {
        const detailRes = await getResourceById(axiosPrivate, idResource)
        const detailData = detailRes.data.data
        if (detailData) {
          setResourceDetail({
            title: detailData.title || '',
            short_desc: detailData.short_desc || '',
            image_url: detailData.image_url || '',
            download_url: detailData.download_url || '',
            is_video: detailData.is_video ?? 0,
            title_fr: detailData.title_fr || null,
            short_desc_fr: detailData.short_desc_fr || null,
          })
        }
      } catch (err) {
        console.error('Failed to load resource detail:', err)
        showSwal('Error', 'Failed to load resource details.', 'error')
      } finally {
        setIsLoading(false)
      }
    }

    loadDetail()
  }, [idResource])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setResourceDetail((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!resourceDetail.title.trim() || isHtmlEmpty(resourceDetail.short_desc)) {
      showSwal('Warning', 'Title and Short Description (EN) are required.', 'warning')
      return
    }

    if (!resourceDetail.image_url.trim() || !resourceDetail.download_url.trim()) {
      showSwal('Warning', 'Image URL and Download URL are required.', 'warning')
      return
    }

    showSwal({
      title: `Are you sure you want to ${isAddMode ? 'create' : 'update'} this resource?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)
      const payload = {
        title: resourceDetail.title.trim(),
        short_desc: resourceDetail.short_desc.trim(),
        image_url: resourceDetail.image_url.trim(),
        download_url: resourceDetail.download_url.trim(),
        is_video: resourceDetail.is_video,
        title_fr: resourceDetail.title_fr?.trim() || null,
        short_desc_fr: resourceDetail.short_desc_fr?.trim() || null,
      }

      if (isAddMode) {
        createResource(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 201) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/resources')
              })
            }
          })
          .catch((err) => {
            console.error('Failed to create resource:', err)
            showSwal('Error', 'Failed to create resource.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        updateResourceById(axiosPrivate, idResource, payload)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error('Failed to update resource:', err)
            showSwal('Error', 'Failed to update resource.', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/resources')
        } else {
          setIsEditMode(false)
          window.location.reload()
        }
      }
    })
  }

  const isFormValid =
    resourceDetail.title.trim() &&
    !isHtmlEmpty(resourceDetail.short_desc) &&
    resourceDetail.image_url.trim() &&
    resourceDetail.download_url.trim()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Resources',
            path: '/resources',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode
          ? 'Add Resource'
          : isEditMode
          ? `Edit Resource: ${resourceDetail.title}`
          : `Resource Detail`}
      </PageTitle>

      <div className='ResourcesDetailPage'>
        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isAddMode
                          ? 'Add New Resource'
                          : isEditMode
                          ? 'Edit Resource Details'
                          : 'Resource Detail'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode && (
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoading ? (
                  isEditMode ? (
                    <Form onSubmit={handleSubmit} className='form-resources'>
                      {/* English Content */}
                      <h3>English Content</h3>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Title <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='Enter resource title'
                            type='text'
                            name='title'
                            value={resourceDetail.title}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Short Description <span className='text-danger'>*</span>
                          </Form.Label>
                          <SummernoteEditor
                            value={resourceDetail.short_desc || ''}
                            onChange={(html) => {
                              const fakeEvent = {
                                target: {
                                  name: 'short_desc',
                                  value: html,
                                },
                              } as any
                              handleInputChange(fakeEvent)
                            }}
                          />
                        </div>
                      </div>

                      <hr />

                      {/* French Content */}
                      <h3>French Content</h3>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>Title (FR)</Form.Label>
                          <Form.Control
                            placeholder='Enter resource title in French (optional)'
                            type='text'
                            name='title_fr'
                            value={resourceDetail.title_fr || ''}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Short Description (FR)
                          </Form.Label>
                          <SummernoteEditor
                            value={resourceDetail.short_desc_fr || ''}
                            onChange={(html) => {
                              const fakeEvent = {
                                target: {
                                  name: 'short_desc_fr',
                                  value: html,
                                },
                              } as any
                              handleInputChange(fakeEvent)
                            }}
                          />
                        </div>
                      </div>

                      <hr />

                      {/* Media */}
                      <h3>Media & Links</h3>
                      <div className='row mb-4'>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Image URL <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='https://example.com/image.png'
                            type='url'
                            name='image_url'
                            value={resourceDetail.image_url}
                            onChange={handleInputChange}
                            required
                          />
                          {resourceDetail.image_url && (
                            <div className='mt-3'>
                              <img
                                src={resourceDetail.image_url}
                                alt='Preview'
                                style={{
                                  maxHeight: '120px',
                                  maxWidth: '200px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                }}
                                onError={(e) => {
                                  ;(e.target as HTMLImageElement).style.display = 'none'
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <div className='col-md-12 mb-5'>
                          <Form.Label className='text-primary me-0 ps-0'>
                            Download / View URL <span className='text-danger'>*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder='https://example.com/resource'
                            type='url'
                            name='download_url'
                            value={resourceDetail.download_url}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className='col-md-6 mb-5 d-flex flex-column justify-content-center'>
                          <Form.Label className='text-primary me-0 ps-0 mb-2'>
                            Type <span className='text-danger'>*</span>
                          </Form.Label>
                          <div
                            className='d-flex align-items-center gap-4'
                            style={{minHeight: '38px'}}
                          >
                            <Form.Check
                              type='radio'
                              id='is_video-no'
                              name='is_video'
                              label='Document'
                              checked={resourceDetail.is_video === 0}
                              onChange={() => setResourceDetail((prev) => ({...prev, is_video: 0}))}
                              className='d-flex align-items-center gap-2'
                            />
                            <Form.Check
                              type='radio'
                              id='is_video-yes'
                              name='is_video'
                              label='Video'
                              checked={resourceDetail.is_video === 1}
                              onChange={() => setResourceDetail((prev) => ({...prev, is_video: 1}))}
                              className='d-flex align-items-center gap-2'
                            />
                          </div>
                        </div>
                      </div>

                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            type='submit'
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <ResourcesDetailView resourceDetail={resourceDetail} />
                  )
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default ResourcesDetailPage
